import type { StudentApplication, UploadFileData } from '../components/Application/types'
import { DocumentTags } from './enums'

export const FILE_UPLOAD_STATUS_UPLOADED = 'UPLOADED'

export const EDUCATION_HISTORY_TRANSCRIPT_UPLOAD_ERROR_MESSAGE =
  'There was an error uploading your transcript. Please try again.'

export const EDUCATION_HISTORY_CERTIFICATE_UPLOAD_ERROR_MESSAGE =
  'There was an error uploading your certificate. Please try again.'

type EducationHistoryEntry = {
  transcripts?: string[]
  certificates?: string[]
}

type ObservableFilesGetter = (filters: {
  fileType?: string | string[]
  sectionReference?: string
}) => Record<string, Omit<UploadFileData, 'file'>>

/**
 * True when every education history entry has transcript/certificate student-doc IDs that exist in
 * `attributes.files` with the expected type and upload status UPLOADED.
 */
export function areEducationHistoryServerDocumentsFullyUploaded(application: StudentApplication): boolean {
  const attrs = application.attributes
  if (!attrs?.educationHistory) {
    return false
  }

  const files = attrs.files ?? {}
  const entries = Object.values(attrs.educationHistory).filter(
    (entry): entry is NonNullable<typeof entry> => entry !== null,
  )

  if (entries.length === 0) {
    return false
  }

  for (const entry of entries as EducationHistoryEntry[]) {
    const transcriptIds = entry.transcripts ?? []
    const certificateIds = entry.certificates ?? []

    if (transcriptIds.length === 0 && certificateIds.length === 0) {
      return false
    }

    for (const fileId of transcriptIds) {
      if (fileId === undefined || fileId === '') {
        return false
      }
      const file = files[fileId] as { type?: unknown; uploadStatus?: string } | undefined
      if (
        !file ||
        String(file.type) !== DocumentTags.TRANSCRIPTS ||
        file.uploadStatus !== FILE_UPLOAD_STATUS_UPLOADED
      ) {
        return false
      }
    }

    for (const fileId of certificateIds) {
      if (fileId === undefined || fileId === '') {
        return false
      }
      const file = files[fileId] as { type?: unknown; uploadStatus?: string } | undefined
      if (
        !file ||
        String(file.type) !== DocumentTags.CERTIFICATE ||
        file.uploadStatus !== FILE_UPLOAD_STATUS_UPLOADED
      ) {
        return false
      }
    }
  }

  return true
}

/**
 * Form submit check: observable files for a section that already exist on the application must be UPLOADED.
 * Entries only in pendingUpload (not yet PATCHed) are skipped so Save & Continue can run the upload flow.
 */
export function areEducationHistoryObservableFilesUploaded(
  application: StudentApplication,
  pendingUpload: Record<string, UploadFileData>,
  getObservableFiles: ObservableFilesGetter,
  fileType: DocumentTags.TRANSCRIPTS | DocumentTags.CERTIFICATE,
  sectionReference: string,
): boolean {
  const observable = getObservableFiles({ fileType, sectionReference })
  const serverFiles = application.attributes?.files ?? {}

  for (const fileId of Object.keys(observable)) {
    if (pendingUpload[fileId]) {
      continue
    }
    const meta = serverFiles[fileId] as { uploadStatus?: string; type?: unknown } | undefined
    if (
      !meta ||
      String(meta.type) !== fileType ||
      meta.uploadStatus !== FILE_UPLOAD_STATUS_UPLOADED
    ) {
      return false
    }
  }

  return true
}
